// DEPENDENCIES ---------------------------------------------------------------- //

// import React from 'react';
import dayjs from 'dayjs';

import { css } from '@emotion/css';
import { TextField, MenuItem, Divider, Theme, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

// COMPONENT ---------------------------------------------------------------- //

interface SearchResultsControlsProps {
  range: string;
  start: string;
  end: string;
  disablePickers: boolean;
  onRangeChange: (range: string) => void;
  onDateChange: (type: 'start' | 'end') => (date: string) => void;
}

const SearchResultsControls = ({
  range,
  start,
  end,
  disablePickers,
  onRangeChange,
  onDateChange,
}: SearchResultsControlsProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  return (
    <>
      <div className={cls.controls}>
        <TextField
          select
          fullWidth
          label='Date Range'
          placeholder='Select a date range...'
          variant='outlined'
          size='small'
          value={range}
          onChange={e => onRangeChange?.(e.target.value)}
        >
          <MenuItem value='default'>
            <span className={cls.rangeText}>Default</span>
            <span className={cls.rangeSubText}>
              {dayjs().subtract(1, 'year').format('MM/DD/YYYY')} - {dayjs().add(30, 'day').format('MM/DD/YYYY')}
            </span>
          </MenuItem>

          <Divider />

          <MenuItem value='nextweek'>
            <span className={cls.rangeText}>Next Week</span>
            <span className={cls.rangeSubText}>
              {dayjs().add(1, 'week').startOf('week').format('MM/DD/YYYY')} -{' '}
              {dayjs().add(1, 'week').endOf('week').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='thisweek'>
            <span className={cls.rangeText}>This Week</span>
            <span className={cls.rangeSubText}>
              {dayjs().startOf('week').format('MM/DD/YYYY')} - {dayjs().endOf('week').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='lastweek'>
            <span className={cls.rangeText}>Last Week</span>
            <span className={cls.rangeSubText}>
              {dayjs().subtract(1, 'week').startOf('week').format('MM/DD/YYYY')} -{' '}
              {dayjs().subtract(1, 'week').endOf('week').format('MM/DD/YYYY')}
            </span>
          </MenuItem>

          <Divider />

          <MenuItem value='nextmonth'>
            <span className={cls.rangeText}>Next Month</span>
            <span className={cls.rangeSubText}>
              {dayjs().add(1, 'month').startOf('month').format('MM/DD/YYYY')} -{' '}
              {dayjs().add(1, 'month').endOf('month').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='thismonth'>
            <span className={cls.rangeText}>This Month</span>
            <span className={cls.rangeSubText}>
              {dayjs().startOf('month').format('MM/DD/YYYY')} - {dayjs().endOf('month').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='lastmonth'>
            <span className={cls.rangeText}>Last Month</span>
            <span className={cls.rangeSubText}>
              {dayjs().subtract(1, 'month').startOf('month').format('MM/DD/YYYY')} -{' '}
              {dayjs().subtract(1, 'month').endOf('month').format('MM/DD/YYYY')}
            </span>
          </MenuItem>

          <Divider />

          <MenuItem value='nextyear'>
            <span className={cls.rangeText}>Next Year</span>
            <span className={cls.rangeSubText}>
              {dayjs().add(1, 'year').startOf('year').format('MM/DD/YYYY')} -{' '}
              {dayjs().add(1, 'year').endOf('year').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='thisyear'>
            <span className={cls.rangeText}>This Year</span>
            <span className={cls.rangeSubText}>
              {dayjs().startOf('year').format('MM/DD/YYYY')} - {dayjs().endOf('year').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='lastyear'>
            <span className={cls.rangeText}>Last Year</span>
            <span className={cls.rangeSubText}>
              {dayjs().subtract(1, 'year').startOf('year').format('MM/DD/YYYY')} -{' '}
              {dayjs().subtract(1, 'year').endOf('year').format('MM/DD/YYYY')}
            </span>
          </MenuItem>

          <Divider />

          <MenuItem value='custom'>Custom</MenuItem>
        </TextField>

        <DatePicker
          disabled={disablePickers}
          maxDate={dayjs(end)}
          label='Start Date'
          orientation='portrait'
          value={dayjs(start)}
          onChange={date => onDateChange?.('start')(date?.toString() || '')}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              variant: 'outlined',
            },
          }}
        />

        <DatePicker
          disabled={disablePickers}
          minDate={dayjs(start)}
          label='End Date'
          orientation='portrait'
          value={dayjs(end)}
          onChange={date => onDateChange?.('end')(date?.toString() || '')}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              variant: 'outlined',
            },
          }}
        />
      </div>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  controls: css`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  `,

  rangeText: css`
    margin-right: 6px;
  `,
  rangeSubText: css`
    font-size: 12px;
    color: ${theme.palette.text.disabled};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default SearchResultsControls;
